import React, { useEffect, useState } from 'react';
import { Box, Button, FormControl, FormControlLabel, FormControlProps, FormHelperText, InputLabel, MenuItem, Select, SelectProps, TextField, TextFieldProps, Theme, Tooltip, TooltipProps, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import Checkbox from '@mui/material/Checkbox';
import { Autocomplete, AutocompleteCloseReason, AutocompleteProps, Chip, ListItemText, Radio, RadioGroup } from '@mui/material';
import {
  Control,
  Controller,
  ControllerFieldState,
  ControllerProps,
  ControllerRenderProps,
  FieldValues,
  UseFormRegister,
  UseFormStateReturn,
} from 'react-hook-form';
import DateFnsUtils from '@date-io/date-fns';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { DatePickerView } from '@mui/lab/DatePicker/shared';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Debouncer } from '../../submodules/fox-typescript/utils/Debouncer';

type Optional<T> = T | null;
interface ValidationProps {
  /**
  control?: any; // TODO type
  /**
   * React hook form property
   */
  errors?: any; // TODO type
  /**
   * React hook form property
   */
  customHelperText?: any; // TODO type
}
interface ComponentProps<T> extends ValidationProps {
  /**
   * Name of the property/variable that will be managed by this input
   */
  propertyName: string;
  /**
   * Label of this input
   */
  label: string;
  /**
   * Input default value
   */
  defaultValue?: T;
}

export interface InputFieldProps<T> extends ComponentProps<T> {
  /**
   * Type of the input. e.g: number, text, etc
   */
  inputType?: string;
  /**
   * `true` if the input is required
   */
  required?: boolean;
  /**
   * `true` if the input is read only
   */
  readOnly?: boolean;
  /**
   * MUI Texfield props
   */
  internalProps?: TextFieldProps;
  /**
   * Rex expression that the input must satisfy to be valid
   */
  pattern?: RegExp;
  /**
   * onChange input function
   */
  onChange?: any;
  /**
   * The size of the text field.
   */
  size?: 'small' | 'medium';
  /**
   * Input style variant
   */
  variant?: FormControlProps['variant'];
  /**
   * Custom class container
   */
  containerClass?: any;
  /**
   * Property needed for compatibility with react-hook-form
   */
  control?: any;
  /**
   * Property needed for compatibility with react-hook-form
   */
  register?: UseFormRegister<FieldValues>;

  /**
   * Property for enable the default value
   */
  useDefaultValue?: boolean;
}

interface InputNumberFieldProps<T> extends InputFieldProps<T> {
  /**
   * If `true`, the min and max properties will not be ignored
   */
  enableEditInputProps?: boolean;
  /**
   * Custom helper text to show if there was an error
   */
  customHelperText?: string;
  /**
   * Min accepted value
   */
  min?: number;
  /**
   * Max accepted value
   */
  max?: number;
  /**
   * Limit number entries to be between the min and max range
   */
  limitRange?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fullWidth: {
      width: '1000px',
    },
  })
);

export const ValidationContext = React.createContext<Optional<ValidationProps>>(null);

interface DateFieldProps extends InputFieldProps<Date> {
  /**
   * Show clear action in picker dialog
   * @default
   * false
   */
  clearable?: boolean;
  /**
   * If `true`, includes time input
   */
  time?: boolean;
  /**
   * Min accepted date
   */
  minDate?: Date;
  /**
   * Max accepted date
   */
  maxDate?: Date;
  /**
   * Array of views to show
   * @type {Array<"year" | "date" | "month">}
   */
  views?: Array<DatePickerView>;
  /**
   * First view to show in DatePicker
   */
  openTo?: DatePickerView;

  utils?: any;
  /**
   * If `true`, shows dates in UTC
   */
  toUTC?: boolean;
  /**
   * If `true`, disable toolbar
   */
  disableToolbar?: boolean;
}
/**
 * This code exports a functional component called DateField that takes in props of type InputFieldProps,
 * where InputFieldProps is a generic type with a Date parameter.
 * The component renders a MUI DatePicker wrapped in a React Hook Form Controller.
 */
export function DateField(props: DateFieldProps) {
  return (
    <div>
      <Controller
        name={props.propertyName}
        control={props.control}
        rules={{ required: props.required }}
        defaultValue={props.required ? props.defaultValue || new Date() : null}
        render={({ field }) => (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              showToolbar={!props.disableToolbar}
              minDate={props.minDate}
              maxDate={props.maxDate}
              views={props.views}
              openTo={props.openTo}
              label={props.label}
              {...field}
              readOnly={props.readOnly}
              disabled={props.readOnly}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant={props.variant ?? 'filled'}
                  error={!!props.errors[props.propertyName]}
                  helperText={props.errors[props.propertyName] && props.label + ' should be provided'}
                />
              )}
            />
            {props.time && (
              <TimePicker
                label={props.label + ' hour'}
                {...field}
                readOnly={props.readOnly}
                disabled={props.readOnly}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant={props.variant ?? 'filled'}
                    error={!!props.errors[props.propertyName]}
                    helperText={props.errors[props.propertyName] && props.label + ' should be provided'}
                  />
                )}
              />
            )}
          </LocalizationProvider>
        )}
      />
    </div>
  );
}

export function CheckboxComponent(props: InputFieldProps<string>) {
  return (
    <div>
      <Controller
        name={props.propertyName}
        control={props.control}
        rules={{ required: props.required }}
        render={({ field: { onChange, value } }) => (
          <FormControlLabel
            label={props.label}
            control={
              <Checkbox
                onChange={(e) => {
                  onChange(e.target.checked);
                  props.onChange && props.onChange(e.target.checked);
                }}
                checked={value}
                sx={{
                  '& .MuiSvgIcon-root': { fontSize: 20 },
                  marginLeft: '10px',
                }}
                disabled={props.readOnly}
              />
            }
          />
        )}
      />
    </div>
  );
}
/**
 * This code exports a functional component called TextFieldComponent that takes in props of type InputFieldProps,
 * where InputFieldProps is a generic type with a string parameter.
 * The component renders a MUI TextField wrapped in a React Hook Form Controller.
 */
export function TextFieldComponent(props: InputFieldProps<string>) {
  const classes = useStyles();
  const context = React.useContext(ValidationContext);
  const finalProps = context ? { ...props, ...context } : props;

  return (
    <div>
      <Controller
        name={finalProps.propertyName}
        control={finalProps.control}
        rules={{ required: finalProps.required, pattern: finalProps.pattern }}
        defaultValue={finalProps.useDefaultValue ? finalProps.defaultValue : ""}
        render={({ field }) => (
          <TextField
            size={finalProps.size}
            label={finalProps.label}
            {...field}
            {...finalProps.internalProps}
            className={props.containerClass ?? classes.fullWidth}
            disabled={!!finalProps.readOnly}
            type={finalProps.inputType || 'text'}
            variant={finalProps.variant ?? 'filled'}
            error={!!finalProps.errors[finalProps.propertyName]}
            helperText={
              finalProps.errors[finalProps.propertyName] && (finalProps.customHelperText || finalProps.label + ' should be provided')
            }
          />
        )}
      />
    </div>
  );
}

NumberField.defaultProps = {
  enableEditInputProps: false,
};
/**
 * This code exports a functional component called NumberField that takes in props of type InputFieldProps,
 * where InputFieldProps is a generic type with a string parameter.
 * The component renders a MUI TextField wrapped in a React Hook Form Controller.
 * Similar to previous function, but here the TextField is configured to only accept numbers
 */
export function NumberField(props: InputNumberFieldProps<string>) {
  const context = React.useContext(ValidationContext);
  const finalProps = context ? { ...props, ...context } : props;

  const isValid = (value: number): boolean => {
    if (finalProps.min && value < finalProps.min) {
      return false;
    }
    if (finalProps.max && value > finalProps.max) {
      return false;
    }
    return true;
  };

  const getHelperText = (): string => {
    return finalProps.min || finalProps.max
      ? `${finalProps.min != undefined ? 'min: ' + finalProps.min : ''} ${finalProps.max ? ' max: ' + finalProps.max : ''}`
      : finalProps.errors[finalProps.propertyName] && (finalProps.customHelperText || finalProps.label + ' should be provided');
  };

  return (
    <div>
      <Controller
        name={finalProps.propertyName}
        control={finalProps.control}
        rules={{ required: finalProps.required, pattern: finalProps.pattern, min: finalProps.min, max: finalProps.max }}
        defaultValue={finalProps.defaultValue}
        render={({ field }) => (
          <TextField
            {...field}
            {...finalProps}
            size={finalProps.size}
            label={finalProps.label}
            variant={finalProps.variant ?? 'filled'}
            type="number"
            disabled={!!finalProps.readOnly}
            onChange={(e) => {
              //We need to do this because otherwise the field itself is not refreshed.This is related to react-hook-form
              if (isValid(parseInt(e.target.value))) {
                field.onChange(e);

                // Extra logic
                finalProps.internalProps?.onChange && finalProps.internalProps.onChange(e);
                finalProps.errors[finalProps.propertyName] = false;
                finalProps.customHelperText = finalProps.customHelperText;
              } else {
                if (finalProps.limitRange) {
                  field.onChange(e.target.value == '' ? '' : field.value);
                } else {
                  finalProps.errors[finalProps.propertyName] = true;
                  field.onChange(e.target.value ?? field.value);
                }
              }
            }}
            error={!!finalProps.errors[finalProps.propertyName]}
            helperText={getHelperText()}
            defaultValue={finalProps.defaultValue}
          />
        )}
      />
    </div>
  );
}

const useAutoCompleteStyles = makeStyles((theme: any) => ({
  container: {
    width: '100%',
  },
}));

interface AutoCompleteProps extends InputFieldProps<string> {
  label: string;
  options: AutoCompleteOption[];
  name?: string;
  id?: string;
  error?: boolean;
  customClassName?: string;
  required?: boolean;
  disabled?: boolean;
  inputSize?: 'small' | 'medium';
}

export const AutoCompleteField = (props: AutoCompleteProps) => {
  const context = React.useContext(ValidationContext);
  const classes = useAutoCompleteStyles();
  const [inputValue, setInputValue] = useState<string>('');
  const [options, setOptions] = useState<AutoCompleteOption[]>([]);

  const { required, disabled, inputSize, customClassName } = props;
  const finalProps = context ? { ...props, ...context } : props;

  useEffect(() => {
    if (!!finalProps.options) {
      const data = finalProps.options;
      setOptions(data);
    } else {
      setOptions([]);
    }
  }, [inputValue]);

  const getDefaultValue = (field: ControllerRenderProps): AutoCompleteOption | undefined => {
    const defaultValue: AutoCompleteOption = {
      label: field.value,
      externalId: `${options.findIndex((opt) => opt.label === field.value)}`,
    };

    return defaultValue;
  };

  return (
    <Controller
      name={finalProps.propertyName}
      control={finalProps.control}
      rules={{ required: finalProps.required }}
      defaultValue=""
      render={({ field }) => {
        return (
          <Autocomplete
            {...field}
            freeSolo
            className={customClassName ?? classes.container}
            options={options}
            fullWidth
            disabled={disabled ?? false}
            size="medium"
            getOptionLabel={(option) => {
              if (!option) {
                return '';
              }

              if (typeof option === 'string') {
                return option;
              } else {
                return option?.label;
              }
            }}
            onInputChange={(event, newInputValue) => {
              if (!Array.isArray(finalProps.options)) {
                setInputValue(newInputValue);
              } else {
                field.onChange(newInputValue.trim());
              }
            }}
            onChange={(_, data) => {
              field.onChange(data?.label);
            }}
            defaultValue={getDefaultValue(field)}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  {...finalProps.internalProps}
                  label={finalProps.label}
                  variant={finalProps.variant ?? 'filled'}
                  error={!!finalProps.errors[finalProps.propertyName]}
                  helperText={finalProps.errors[finalProps.propertyName] && finalProps.label + ' should be provided'}
                  required={required ?? true}
                  size={inputSize ?? 'small'}
                />
              );
            }}
          />
        );
      }}
    />
  );
};

interface SelectOption {
  value: string;
  label: string;
}

const selectFieldStyle = makeStyles((theme: Theme) =>
  createStyles({
    formControl: {
      margin: theme.spacing(1),
      minWidth: 120,
      width: '100%',
    },
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
  })
);

interface SelectFieldProps extends InputFieldProps<string> {
  /**
   * Options that the field must display
   */
  options: SelectOption[];
  /**
   * MUI Select props
   */
  internalSelectProps?: Partial<SelectProps>;
  /**
   * Custom class container
   */
  containerClass?: any;
  /**
   * Option to accept multiple select behaivor
   */
  isMultiple?: boolean;
}

/**
 * This function defines a SelectFieldComponent that renders a dropdown select field
 * It takes in a props object with the following properties:
 * - propertyName: the name of the property that this select field corresponds to
 * - control: the form control for this select field
 * - required: a boolean indicating whether this select field is required
 * - errors: an object containing any validation errors for this select field
 * - label: the label to display for this select field
 * - options: an array of objects representing the options for this select field
 */
export function SelectFieldComponent(props: SelectFieldProps) {
  const classes = selectFieldStyle();
  const context = React.useContext(ValidationContext);
  const finalProps = context ? { ...props, ...context } : props;

  return (
    <div>
      <Controller
        name={finalProps.propertyName}
        control={finalProps.control}
        rules={{ required: finalProps.required }}
        defaultValue={finalProps.defaultValue}
        render={({ field }) => (
          <FormControl
            className={props.containerClass ?? classes.formControl}
            variant={finalProps.variant ?? 'filled'}
            error={!!finalProps.errors[finalProps.propertyName]}
            disabled={finalProps.readOnly}
          >
            <InputLabel>{finalProps.label}</InputLabel>
            <Select
              {...field}
              {...finalProps.internalSelectProps}
              label={props.label}
              multiple={props.isMultiple}
              value={props.isMultiple ? (Array.isArray(field.value) ? field.value : []) : field.value} //We need to do this because otherwise the value of the select will be a string instead of an array of strings on multiple select
              onChange={(e) => {
                //We need to do this because otherwise the field itself is not refreshed.This is related to react-hook-form
                field.onChange(e);

                // Extra logic
                // @ts-ignore
                finalProps.internalSelectProps?.onChange && finalProps.internalSelectProps.onChange(e, field);
              }}
            >
              {finalProps.options.map((o, index) => (
                <MenuItem value={o.value} key={index}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
            {!!finalProps.errors[finalProps.propertyName] && (
              <FormHelperText>{finalProps.errors[finalProps.propertyName] && finalProps.label + ' must be provided'}</FormHelperText>
            )}
          </FormControl>
        )}
      />
    </div>
  );
}

interface AutoCompleteFieldProps extends InputFieldProps<string> {
  /**
   * options can be an Array of AutoCompleteOption or an async function that will requests the values to backend using the input text
   */
  options: AutoCompleteOption[] | ((text: string) => Promise<AutoCompleteOption[]>);
  /**
   * if a function is passed in options property, we can ajust the debounce ms applied in each call (default to 800ms)
   */
  debounceMs?: number;
  /**
   * Allow to display the previous value in the textfield
   */
  editWithPreviousValue?: boolean;
  /**
   * onChange input function
   */
  onChange?: (data: any) => void;

  /**
   * Enable error styles
   */
  error?: boolean;

  /**
   * Used to map the value in the controller to an AutoCompleteOption if necessary
   */
  mapControllerValueToAutoCompleteOption?: (value?: any) => AutoCompleteOption | undefined;
    configurationToolTip?: ConfigurationToolTip;
}

export interface AutoCompleteOption {
  label: string;
  externalId: string;
  isPromoter?: boolean;
labelToolTip?: string;
}

export interface ConfigurationToolTip {
  placement?: TooltipProps['placement'];
  arrow?: boolean;
  followCursor?: boolean;
}

const debouncer = new Debouncer(800);

export function AutoCompleteComponent(props: AutoCompleteFieldProps) {
  const classes = useStyles();
  const context = React.useContext(ValidationContext);
  const finalProps = context ? { ...props, ...context } : props;
    const configurationToolTip = finalProps.configurationToolTip;

  const [options, setOptions] = useState<AutoCompleteOption[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const getDefaultValue = (field: ControllerRenderProps): AutoCompleteOption | undefined => {
    if (finalProps.editWithPreviousValue) {
      const defaultValue: AutoCompleteOption = {
        label: field.value,
        externalId: `${options.findIndex((opt) => opt.label === field.value)}`,
      };

      return defaultValue;
    } else if (finalProps.mapControllerValueToAutoCompleteOption) {
      return finalProps.mapControllerValueToAutoCompleteOption(field.value);
    }

    return undefined;
  };

  useEffect(() => {
    props.debounceMs && debouncer.setLimit(props.debounceMs);
  }, []);

  useEffect(() => {
    if (!Array.isArray(finalProps.options)) {
      if (!inputValue) {
        setOptions([]);
      } else {
        setLoading(true);
        debouncer.debounceKeepingLastCall(async () => {
          try {
            const data = await (finalProps.options as any)(inputValue);
            setOptions(data);
          } catch {
            console.error('[ERROR] Error geting options for autocomplete: ' + props.propertyName);
          } finally {
            setLoading(false);
          }
        });
      }
    }
  }, [inputValue]);

  const getNestedError = (errors: any, propertyName: string) => {
    const propertyNames = propertyName.split('.');
    let nestedErrors = errors;
    for (const name of propertyNames) {
      if (nestedErrors && nestedErrors[name]) {
        nestedErrors = nestedErrors[name];
      } else {
        nestedErrors = null;
        break;
      }
    }
    return nestedErrors;
  };

  return (
    <div>
      <Controller
        name={finalProps.propertyName}
        control={finalProps.control}
        rules={{ required: finalProps.required }}
        defaultValue={finalProps.mapControllerValueToAutoCompleteOption ? finalProps.defaultValue : ''}
        render={(props) => (
          <Autocomplete
            onInputChange={(event, newInputValue) => {
              if (!Array.isArray(finalProps.options)) {
                setInputValue(newInputValue);
              }
            }}
            defaultValue={getDefaultValue(props.field)}
            loading={loading}
            options={loading ? [] : Array.isArray(finalProps.options) ? finalProps.options : options}
            getOptionLabel={(option) => option.label}
            onChange={(_, data) => {
              //@ts-ignore
              props.field.onChange(data?.externalId);
              finalProps.onChange && finalProps.onChange(data);
            }}
            disabled={!!finalProps.readOnly}
            filterOptions={Array.isArray(finalProps.options) ? undefined : (x) => x}
            renderOption={(renderProps, autoCompleteOption) => {
              if (!configurationToolTip || !autoCompleteOption.labelToolTip) {
                return <li {...renderProps}>{autoCompleteOption.label}</li>;
              }

              const { key, ...autoCompleteOptionProps } = renderProps as any;

              return (
                <Tooltip
                  key={key}
                  title={autoCompleteOption.labelToolTip}
                  placement={configurationToolTip.placement ?? 'right'}
                  arrow={configurationToolTip.arrow ?? true}
                  followCursor={configurationToolTip.followCursor ?? true}
                >
                  <li {...autoCompleteOptionProps}>{autoCompleteOption.label}</li>
                </Tooltip>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                {...finalProps.internalProps}
                label={finalProps.label}
                variant={finalProps.variant ?? 'filled'}
                className={classes.fullWidth}
                error={finalProps.error ?? !!getNestedError(finalProps.errors, finalProps.propertyName)}
                helperText={getNestedError(finalProps.errors, finalProps.propertyName) && finalProps.label + ' should be provided'}
              />
            )}
          />
        )}
      />
    </div>
  );
}

type IncludeSelectControl<T, U> =
  | {
      /**
       * Define this propety to true if you want to use this component with react-hook-form
       */
      includeControl: true;
      control: Control<FieldValues>;
      propertyName: string;
      register: UseFormRegister<FieldValues>;
      required?: boolean;
      mapOptionToValue: (o: T) => U;
      mapValueToOption: (v: U) => T;
      isOptionEqualToValue: (o: T, v: ReturnType<(o: T) => U>) => boolean;
    }
  | {
      /**
       * Define this propety to true if you want to use this component with react-hook-form
       */
      includeControl: false;
      values: T[];
      onChange: (value: T[]) => void;
      isOptionEqualToValue: (o: T, v: T) => boolean;
    };

export interface MultiSelectProps<T> {
  label?: string;
  options: T[];
  getOptionLabel: (o: T) => string;
  defaultValues?: T[];
  internalProps?: Omit<
    AutocompleteProps<T, true, boolean | undefined, false>,
    | 'multiple'
    | 'freeSolo'
    | 'defaultValue'
    | 'options'
    | 'getOptionLabel'
    | 'onChange'
    | 'renderInput'
    | 'disableCloseOnSelect'
    | 'onClose'
    | 'onOpen'
  >;
  textFieldProps?: Omit<TextFieldProps, 'label' | 'variant' | 'className' | 'error' | 'helperText'>;
  variant?: 'filled' | 'outlined' | 'standard';
  error?: boolean;
}

export function MultiSelectComponent<T, U>(props: MultiSelectProps<T> & IncludeSelectControl<T, U>) {
  const contextProps = React.useContext(ValidationContext);

  const getNestedError = (errors?: any) => {
    if (!props.includeControl) {
      return null;
    }

    const propertyNames = props.propertyName.split('.');
    let nestedErrors = errors;
    for (const name of propertyNames) {
      if (nestedErrors && nestedErrors[name]) {
        nestedErrors = nestedErrors[name];
      } else {
        nestedErrors = null;
        break;
      }
    }
    return nestedErrors;
  };

  var Child = (childProps: {
    field?: ControllerRenderProps<FieldValues, string>;
    onChange: (value: T[]) => void;
    isOptionEqualToValue: (o: T, v: T) => boolean;
    values: T[];
  }) => {
    return (
      <Autocomplete
        {...childProps.field}
        value={childProps.values}
        multiple
        disableCloseOnSelect
        defaultValue={props.defaultValues}
        options={props.options}
        getOptionLabel={props.getOptionLabel}
        isOptionEqualToValue={childProps.isOptionEqualToValue}
        onChange={(_, data) => {
          //@ts-ignore
          childProps.onChange(data);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={props.label}
            variant={props.variant ?? 'filled'}
            error={props.error ?? !!getNestedError(contextProps?.errors)}
            helperText={getNestedError(contextProps?.errors) && props.label + ' should be provided'}
            {...props.textFieldProps}
          />
        )}
        {...props.internalProps}
      />
    );
  };

  if (props.includeControl == true) {
    return (
      <Controller
        name={props.propertyName}
        control={props.control}
        rules={{ required: props.required }}
        defaultValue={props.defaultValues ?? []}
        render={({ field }) => (
          <Child
            field={field}
            onChange={(options) => field.onChange(options.map((o) => props.mapOptionToValue(o)))}
            isOptionEqualToValue={(o, v) => props.isOptionEqualToValue(o, props.mapOptionToValue(v))}
            values={field.value.map((v: U) => props.mapValueToOption(v))}
          />
        )}
      />
    );
  } else {
    return <Child onChange={props.onChange} isOptionEqualToValue={props.isOptionEqualToValue} values={props.values} />;
  }
}

export function UploadFileComponent(props: InputFieldProps<string>) {
  const classes = fileStyles();
  const context = React.useContext(ValidationContext);
  const finalProps = context ? { ...props, ...context } : props;

  return (
    <Controller
      name={finalProps.propertyName}
      control={finalProps.control}
      rules={{ required: finalProps.required }}
      render={(field) => (
        <>
          <Typography className={classes.title}>{finalProps.label}</Typography>
          <Box className={classes.inputBox}>
            <Button
              startIcon={<FileUploadIcon />}
              variant="contained"
              component="label"
              className={classes.inputButton}
              // on error, button text color changes to red
              style={!!finalProps.errors[finalProps.propertyName] ? { color: '#f44336' } : {}}
            >
              <input
                type="file"
                onChange={(e) => {
                  field.field.onChange(e.target.files?.item(0));
                }}
                hidden
              />
              Choose File
            </Button>
            <Box className={classes.inputTextBox}>
              {!!finalProps.errors[finalProps.propertyName] ? (
                <span className={classes.errorMessage}>File should be provided</span>
              ) : !field.field.value ? (
                <Typography>No file chosen</Typography>
              ) : (
                <Typography>{field.field.value.name}</Typography>
              )}
            </Box>
          </Box>
        </>
      )}
    />
  );
}

const fileStyles = makeStyles((theme: Theme) =>
  createStyles({
    inputBox: {
      display: 'flex',
      alignItems: 'center',
    },
    inputButton: {
      marginLeft: '10px',
      marginTop: '5px',
      width: '30%',
    },
    inputTextBox: {
      width: '70%',
      marginLeft: '10px',
    },
    title: {
      marginLeft: '10px',
      fontSize: '16px',
    },
    errorMessage: {
      color: '#f44336',
      marginTop: '3px',
      fontFamily: 'Helvetica',
      letterSpacing: '0.03333rem',
    },
  })
);

type IncludeRadioControl<T> =
  | {
      /**
       * Define this propety to true if you want to use this component with react-hook-form
       */
      includeControl: true;
      control: Control<FieldValues>;
      propertyName: string;
      register: UseFormRegister<FieldValues>;
      required?: boolean;
    }
  | {
      /**
       * Define this propety to true if you want to use this component with react-hook-form
       */
      includeControl: false;
      value: T;
    };

interface RadioOption<T> {
  value: T;
  label: string;
}

interface RadioProps<T> {
  label?: string;
  readOnly?: boolean;
  options: RadioOption<T>[];
  defaultValue?: T;
  onChange: (value: T) => void;
  comparator: (value1: T, value2: T) => boolean;
  row?: boolean;
}

const radioStyles = makeStyles((theme: Theme) =>
  createStyles({
    inputMargin: {
      margin: theme.spacing(1),
    },
    label: {
      marginLeft: 0,
    },
  })
);

export function RadioComponent<T>(props: RadioProps<T> & IncludeRadioControl<T>) {
  const context = React.useContext(ValidationContext);
  const classes = radioStyles();

  var Child = (childProps: { value: T; onChange: (value: T) => void }) => (
    <RadioGroup
      value={props.options.findIndex((e) => props.comparator(e.value, childProps.value))}
      onChange={(e) => {
        childProps.onChange(props.options[parseInt(e.target.value)].value);
      }}
      row={props.row}
      className={classes.inputMargin}
    >
      {props.options.map((option, index) => (
        <FormControlLabel
          className={classes.label}
          key={index}
          value={index}
          control={<Radio />}
          label={option.label}
          disabled={props.readOnly}
        />
      ))}
    </RadioGroup>
  );

  if (props.includeControl) {
    return (
      <>
        <Controller
          name={props.propertyName}
          control={props.control}
          rules={{ required: props.required }}
          defaultValue={props.defaultValue}
          render={({ field: { onChange, value } }) => <Child onChange={onChange} value={value} />}
        />
      </>
    );
  }

  // @ts-ignore
  return <Child onChange={props.onChange} value={props.value} />;
}
